import * as React from "react";
import Markdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { css } from "@emotion/react";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";

export interface IProps {
  faqs: any[];
}

export const LpCruiseFaqList: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { faqs } = props;

  return (
    <div css={listStyle}>
      {faqs.map((faq: any) => {
        return (
          <div key={faq.id} css={listItemStyle}>
            <div css={questionStyle}>{faq.questionText}</div>
            <div css={answerStyle}>
              <Markdown remarkPlugins={[remarkBreaks]} skipHtml={false}>
                {faq.answerText}
              </Markdown>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const listStyle = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 3rem;
`;
const listItemStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body1};
  border-top: 1px solid ${borderColor.secondaryLightColor};
  padding-top: 2.5rem;
  &:first-of-type {
    border-top: none;
    padding-top: ${space.atom0_5x};
  }
`;
const questionStyle = css`
  font-size: 1.1875rem;
  line-height: 1.4;
  font-weight: ${fontWeight.medium};
  margin-bottom: ${space.atom4x};
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.headline};
    line-height: ${lineHeight.headline2};
  }
`;
const answerStyle = css`
  color: ${textColor.primaryDarkColor};
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body1};
  // for markdown
  h1 {
    font-size: ${fontSize.headline};
    line-height: ${lineHeight.headline2};
    margin-bottom: ${space.atom1_5x};
    font-weight: ${fontWeight.medium};
  }
  h2 {
    font-size: 1.1875rem;
    line-height: 1.4;
    margin-top: ${space.atom2_5x};
    margin-bottom: ${space.atom1_5x};
    font-weight: ${fontWeight.medium};
  }
  h3,
  h4,
  h5,
  h6 {
    font-size: ${fontSize.mediumHeading};
    line-height: ${lineHeight.mediumHeadline2};
    margin-top: ${space.atom2x};
    margin-bottom: ${space.atom};
    font-weight: ${fontWeight.medium};
  }
  p {
    margin-top: ${space.atom};
    margin-bottom: ${space.atom};
  }
  ul,
  ol {
    margin: ${space.atom} 0;
    padding-left: 1.3em;
  }
  a {
    color: ${accentColor.primaryColor};
    text-decoration: underline;
    &:hover {
      color: ${accentColor.primaryDarkColor};
    }
  }
  strong {
    font-weight: ${fontWeight.medium};
  }
  pre {
    padding: ${space.atom2x};
    overflow: auto;
    font-size: 85%;
    line-height: 1.45;
    background-color: rgb(0 0 0 / 0.04);
    border-radius: ${borderRadius.normal};
    margin: ${space.atom} 0;
    code {
      padding: 0;
      font-size: inherit;
      background-color: transparent;
      border-radius: 0;
    }
  }
  code {
    padding: 0.2em 0.4em;
    font-size: 85%;
    background-color: rgb(0 0 0 / 0.04);
    border-radius: ${borderRadius.normal};
  }
  hr {
    background-color: rgb(0 0 0 / 0.1);
    border: none;
    margin: 1em 0;
  }
  blockquote {
    margin: ${space.atom} 0;
    padding: 0 1em;
    color: ${textColor.secondaryDarkColor};
    border-left: 0.25em solid ${borderColor.secondaryLightColor};
  }
`;
